import type { FiberCocModelsParams, FiberCocModelsQueryInput } from '../-types';

/**
 * The only columns the route will sort by — `ADMIN_FIBER_COC_MODEL_SORT_COLUMNS`
 * in `modules/fiber`. The querystring pattern is anchored, so a value outside
 * the set fails validation and 400s the whole list rather than being ignored;
 * an unlisted sort is dropped here instead.
 */
const SORTABLE = new Set(['name', 'updatedAt']);

/**
 * The only column the route filters on. Other keys are dropped, and the match
 * is a case-insensitive substring.
 */
const FILTERABLE = new Set(['name']);

/** The route caps `count` at 500. */
export const MAX_COUNT = 500;

/** Keeps a `field:DIR` pair only when the field is one the route accepts. */
function toSort(sort: string | undefined): string | undefined {
  if (!sort) return undefined;
  const [field, dir] = sort.split(':');
  if (!field || !SORTABLE.has(field)) return undefined;
  return dir === 'DESC' ? `${field}:DESC` : `${field}:ASC`;
}

/**
 * Serialises the table's filter map into the `filter` JSON blob the route
 * parses, keeping only the keys it maps.
 *
 * `useTable` joins multi-values with a comma, but the filter is a LIKE match,
 * so only the first value is meaningful — a joined `"a,b"` would match nothing.
 */
export function buildFiberCocModelsFilterJson(
  filters: Record<string, string[]> | undefined,
): string | undefined {
  if (!filters) return undefined;
  const out: Record<string, string> = {};
  for (const [key, values] of Object.entries(filters)) {
    if (!FILTERABLE.has(key)) continue;
    const first = values?.[0]?.trim();
    if (first) out[key] = first;
  }
  return Object.keys(out).length > 0 ? JSON.stringify(out) : undefined;
}

/**
 * Turns table state into the endpoint's parameters — the single conversion
 * point, so the route loader and the page's hook land on the same cache entry.
 *
 * **`page` is decremented.** The route's `page` is 0-based while `useTable`
 * counts from 1.
 */
export function toFiberCocModelsParams(
  input: FiberCocModelsQueryInput,
): FiberCocModelsParams {
  const sort = toSort(input.sort);
  const filter = buildFiberCocModelsFilterJson(input.filters);

  return {
    page: Math.max(0, input.page - 1),
    count: Math.min(Math.max(1, input.limit), MAX_COUNT),
    ...(sort ? { sort } : {}),
    ...(filter ? { filter } : {}),
  };
}

export function buildFiberCocModelsQs(params: FiberCocModelsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
